// Layer 4 (UI) — the Pipeline tab, per the locked comp (variant A, picked
// 25 Sep): the four stages as the product's one strip object (Advisory's
// `PipelineStrip`, given this route's stages), then the selected stage's rows.
// A row opens in the register. Presentational only.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { PipelineStrip, type PipelineStripStage } from '@/versions/v1/modules/advisory/PipelineStrip';
import { CARD_TABLE, DowntimeCard, SYN_CHIP } from '@/versions/v1/modules/downtime/DowntimeCard';
import { PIPELINE_STAGES, STAGE_LABEL, improvementPipeline, improvementPipelineStrip } from './ImprovementPipeline.transform';
import type { ImprovementStage } from './seed';

const STAGES: readonly PipelineStripStage<ImprovementStage>[] = PIPELINE_STAGES.map((stage) => ({
  id: stage,
  label: STAGE_LABEL[stage],
  actors: [],
}));

export function ImprovementPipeline({
  stage,
  onSelectStage,
  onOpenOpportunity,
}: {
  stage: ImprovementStage;
  onSelectStage: (stage: ImprovementStage) => void;
  onOpenOpportunity: (id: string) => void;
}) {
  const view = improvementPipeline();
  const cells = improvementPipelineStrip();
  const lane = view.lanes.find((item) => item.stage === stage) ?? view.lanes[0]!;

  return (
    <div className="space-y-3">
      <PipelineStrip<ImprovementStage>
        stages={STAGES}
        activeStage={lane.stage}
        cells={cells}
        onSelect={onSelectStage}
        navLabel="Improvement pipeline"
        idPrefix="cost-pipeline"
        panelId="cost-pipeline-stage-panel"
      />
      <Commentable name="ImprovementPipeline">
        <div id="cost-pipeline-stage-panel" role="tabpanel" aria-label={lane.label}>
          <DowntimeCard title={lane.label} meta={`${lane.count} of ${view.total}`} actions={SYN_CHIP}>
            <div className={CARD_TABLE}>
              <Table wrap={false} clickableRows className="tabular-nums" aria-label={`${lane.label} opportunities`}>
                <THead>
                  <TR>
                    <TH>ID</TH>
                    <TH>Opportunity</TH>
                    <TH className="text-right">Benefit / yr</TH>
                    <TH className="text-right">Capex</TH>
                    <TH className="text-right">Payback</TH>
                  </TR>
                </THead>
                <TBody>
                  {lane.rows.map((row) => (
                    <TR key={row.id} onRowClick={() => onOpenOpportunity(row.id)} data-pipeline-row={row.id}>
                      <TD className="whitespace-nowrap font-mono text-xs text-ink-primary">{row.id}</TD>
                      <TD className="font-semibold text-ink-primary">{row.opportunity}</TD>
                      <TD className="whitespace-nowrap text-right text-ink-primary">{row.benefitPerYear}</TD>
                      <TD className="whitespace-nowrap text-right">{row.capex}</TD>
                      <TD className="whitespace-nowrap text-right font-semibold text-ink-primary">{row.paybackYears}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </DowntimeCard>
        </div>
      </Commentable>
    </div>
  );
}
